// Self-hosted fonts (was a render-blocking Google Fonts request chain). Bundled into
// the hashed CSS; browsers still pick only the subsets they need via unicode-range.
// OpenDyslexic (the third font preference) is served from public/fonts via style.css.
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import '@fontsource/atkinson-hyperlegible/400.css'
import '@fontsource/atkinson-hyperlegible/700.css'

import * as ReactDOM from 'react-dom/client'
import { RouterProvider } from 'react-router'

import { configureAuth } from '@rdm/shared-ui'

import { API_URL, UQRDM_SSO_COOKIE_REFRESH_NAME } from './lib/config'
import Sentry from './lib/sentry'
import { router } from './routes'

// Configure auth before the router mounts so route loaders can call attemptAuth
// without threading config through.
configureAuth({ apiUrl: API_URL, cookieName: UQRDM_SSO_COOKIE_REFRESH_NAME })

// Recover from stale code-split chunks after a deploy. When a tab loaded before a
// deploy navigates to a not-yet-fetched route, the old hashed chunk is gone and the
// dynamic import fails (404, or the SPA fallback returns index.html as text/html).
// Vite fires `vite:preloadError`; reload once to pull the fresh index.html + chunk map.
// A sessionStorage guard prevents an infinite reload loop if the chunk is genuinely
// missing rather than just stale; it is cleared on a successful load so a later deploy
// can self-heal again.
window.addEventListener('vite:preloadError', () => {
  if (sessionStorage.getItem('chunk-reload-attempted')) return
  sessionStorage.setItem('chunk-reload-attempted', '1')
  window.location.reload()
})
window.addEventListener('load', () => sessionStorage.removeItem('chunk-reload-attempted'))

// Browser translation extensions (e.g. Google Translate) swap text nodes for their own
// wrappers; React then unmounts a node the translation layer moved and removeChild/
// insertBefore throw a NotFoundError mid-commit that trips the route ErrorBoundary.
// No-op only the already-detached case so normal commits keep native behaviour. [RDM-FRONTEND-3MR]
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) return child
    return originalRemoveChild.call(this, child) as T
  }
  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) return newNode
    return originalInsertBefore.call(this, newNode, referenceNode) as T
  }
}

// Wire React 19's error hooks into Sentry so render-time errors carry React's
// component stack — without this, errors thrown inside react-dom (e.g. "Objects are
// not valid as a React child") reach Sentry with no first-party frame and no way to
// tell which component rendered them.
const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement, {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
})

root.render(<RouterProvider router={router} />)
